import React, { useEffect, useState, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import FontFamily from "@tiptap/extension-font-family";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import { Card, Container, Row, Col, Badge, Button, Spinner, Form, Modal } from "react-bootstrap";
import {
  apiBlogGetAll,
  apiBlogGetById,
  apiBlogCreate,
  apiBlogUpdate,
  apiBlogPublish,
  apiBlogUnpublish,
  apiBlogDelete,
  apiBlogDiscardMedia,
} from "../api/index";
import AppSelect from "../components/ui/AppSelect";
import { toast } from "react-toastify";

// ── ResizableImage node ────────────────────────────────────────────────────
const ResizableImageView = ({ node, updateAttributes, deleteNode, selected }) => {
  const { src, alt, width, align } = node.attrs;
  const startX = useRef(0);
  const startW = useRef(0);
  const imageWrapRef = useRef(null);
  const onResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startX.current = e.clientX;
    startW.current = imageWrapRef.current?.getBoundingClientRect().width || parseInt(width) || 400;
    const onMove = (ev) => {
      const delta = ev.clientX - startX.current;
      const newW = Math.max(80, startW.current + delta);
      updateAttributes({ width: newW + "px" });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  const textAlignMap = { left: "left", center: "center", right: "right" };
  const textAlign = textAlignMap[align] || "left";
  return (
    <NodeViewWrapper style={{ display: "block", width: "100%", textAlign }}>
      <div ref={imageWrapRef} style={{ position: "relative", display: "inline-block", width: width || "auto", maxWidth: "100%", outline: selected ? "2px solid #0d2137" : "none", borderRadius: 4 }}>
          {/* Alignment toolbar — only when selected */}
          {selected && (
            <div style={{ position: "absolute", top: -34, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 2, background: "#fff", border: "1px solid #ddd", borderRadius: 6, padding: "3px 5px", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", zIndex: 30 }}>
              {[
                { label: "Full", value: "100%" },
                { label: "Medium", value: "70%" },
                { label: "Small", value: "45%" },
              ].map(({ label, value }) => (
                <button key={value} type="button"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); updateAttributes({ width: value }); }}
                  style={{ border: "none", borderRadius: 3, padding: "2px 5px", cursor: "pointer", fontSize: "0.68rem", background: width === value ? "#e3ecf5" : "transparent", fontWeight: width === value ? 700 : 400 }}>
                  {label}
                </button>
              ))}
              <span style={{ width: 1, background: "#ddd", margin: "1px 2px" }} />
              {["left","center","right"].map((a) => (
                <button key={a} type="button"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); updateAttributes({ align: a }); }}
                  style={{ border: "none", borderRadius: 3, padding: "2px 5px", cursor: "pointer", fontSize: "0.7rem", background: align === a ? "#e3ecf5" : "transparent", fontWeight: align === a ? 700 : 400 }}>
                  {a === "left" ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>
                  ) : a === "center" ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>
                  )}
                </button>
              ))}
              <span style={{ width: 1, background: "#ddd", margin: "1px 2px" }} />
              {/* Remove the image from the article. The file leaves Cloudinary
                  on save, once the server sees the body no longer uses it. */}
              <button type="button" title="Remove image"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); deleteNode?.(); }}
                style={{ border: "none", borderRadius: 3, padding: "2px 5px", cursor: "pointer", fontSize: "0.7rem", background: "transparent", color: "#c62828" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              </button>
            </div>
          )}
          <img src={src} alt={alt || ""} style={{ width: "100%", maxWidth: "100%", height: "auto", display: "inline-block", borderRadius: 4, verticalAlign: "top" }} />
          {/* Resize handle */}
          <div onMouseDown={onResizeMouseDown}
            style={{ position: "absolute", right: -5, bottom: -5, width: 14, height: 14, background: "#0d2137", borderRadius: "50%", cursor: "ew-resize", zIndex: 10, border: "2px solid #fff", display: selected ? "block" : "none" }}
          />
        </div>
    </NodeViewWrapper>
  );
};

const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null, parseHTML: (el) => el.getAttribute("width") || el.style.width || null, renderHTML: (attrs) => attrs.width ? { width: attrs.width, style: `width:${attrs.width}` } : {} },
      align: { default: "left", parseHTML: (el) => el.getAttribute("data-align") || "left", renderHTML: (attrs) => ({ "data-align": attrs.align || "left" }) },
    };
  },
  addNodeView() { return ReactNodeViewRenderer(ResizableImageView); },
});

// ── IframeEmbed node ─────────────────────────────────────────────────────────
const IframeEmbed = Node.create({
  name: "iframeEmbed",
  group: "block",
  atom: true,
  addAttributes() {
    return { src: { default: null }, width: { default: "100%" }, height: { default: "400px" } };
  },
  parseHTML() { return [{ tag: "div[data-iframe-embed] iframe" }]; },
  renderHTML({ HTMLAttributes }) {
    return ["div", { "data-iframe-embed": "", style: "position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1rem 0" },
      ["iframe", mergeAttributes({ src: HTMLAttributes.src, style: "position:absolute;top:0;left:0;width:100%;height:100%;border:none", allowfullscreen: "true", allow: "accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" })]
    ];
  },
  addCommands() {
    return { insertIframe: (attrs) => ({ commands }) => commands.insertContent({ type: this.name, attrs }) };
  },
});

const FontSize = Extension.create({
  name: "fontSize",
  addGlobalAttributes() {
    return [{ types: ["textStyle"], attributes: { fontSize: { default: null,
      parseHTML: (el) => el.style.fontSize || null,
      renderHTML: (attrs) => attrs.fontSize ? { style: `font-size:${attrs.fontSize}` } : {},
    } } }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize: () => ({ chain }) => chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "lusuku-clou";

const CLOUDINARY_PALETTE = {
  window: "#FFFFFF", windowBorder: "#0d2137",
  tabIcon: "#0d2137", menuIcons: "#0d2137",
  textDark: "#000000", textLight: "#FFFFFF",
  link: "#0d2137", action: "#1a8917",
  inProgress: "#0d2137", complete: "#1a8917",
  error: "#c62828", sourceBg: "#F5F5F5",
};

/**
 * One rule per kind of blog upload. The widget config, the dimension gate, the
 * error copy and the delivery transform all read from this table, so changing
 * a size is a one-line edit instead of three near-identical configs kept in
 * step by hand.
 *
 * `exactWidth/exactHeight` gate hard: the cover image is what LinkedIn and
 * WhatsApp render, and anything that is not 1200 x 630 either gets letterboxed
 * or cropped by the platform, so a wrong-size file is refused rather than
 * silently reshaped.
 */
const BLOG_MEDIA_RULES = {
  ogImage: {
    label: "Cover image",
    folder: "blog/og-images",
    formats: ["jpg", "jpeg", "png", "webp"],
    maxBytes: 5000000,
    exactWidth: 1200,
    exactHeight: 630,
    transform: "c_fill,g_auto,w_1200,h_630,q_auto,f_auto",
  },
  inlineImage: {
    label: "Body image",
    folder: "blog/inline-images",
    formats: ["jpg", "jpeg", "png", "webp", "gif"],
    maxBytes: 8000000,
    minWidth: 800,
    maxWidth: 4000,
    maxHeight: 4000,
    transform: "q_auto,f_auto",
  },
  attachment: {
    label: "Attachment",
    folder: "blog/attachments",
    formats: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"],
    maxBytes: 20000000,
    resourceType: "auto",
  },
};

/* Returns an operator-readable reason the file was refused, or null if it
   passes. Used after the upload lands, because Cloudinary's own pre-upload
   check does not cover the URL and camera sources. */
const dimensionError = (rule, width, height) => {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (rule.exactWidth && (width !== rule.exactWidth || height !== rule.exactHeight)) {
    return `${rule.label} must be exactly ${rule.exactWidth} x ${rule.exactHeight} px. `
      + `The file you picked is ${width} x ${height} px, so it was not attached. `
      + `Resize or crop it to ${rule.exactWidth} x ${rule.exactHeight} and try again.`;
  }
  if (rule.minWidth && width < rule.minWidth) {
    return `${rule.label} must be at least ${rule.minWidth}px wide. `
      + `The file you picked is ${width} x ${height} px, so it was not inserted.`;
  }
  if (rule.maxWidth && (width > rule.maxWidth || height > rule.maxHeight)) {
    return `${rule.label} must be no larger than ${rule.maxWidth} x ${rule.maxHeight} px. `
      + `The file you picked is ${width} x ${height} px, so it was not inserted.`;
  }
  return null;
};

const widgetOptionsFor = (rule) => ({
  cloudName: CLOUDINARY_CLOUD_NAME,
  uploadPreset: CLOUDINARY_UPLOAD_PRESET,
  folder: rule.folder,
  maxFileSize: rule.maxBytes,
  clientAllowedFormats: rule.formats,
  multiple: false,
  sources: rule.resourceType === "auto" ? ["local", "url"] : ["local", "url", "camera"],
  showPoweredBy: false,
  ...(rule.resourceType ? { resourceType: rule.resourceType } : {}),
  // Cloudinary's own pre-upload gate, so a wrong-size local file is stopped
  // before it costs an upload. validateMaxWidthHeight makes the widget refuse
  // instead of quietly scaling the file down to the maximum.
  ...(rule.exactWidth
    ? {
        minImageWidth: rule.exactWidth,
        minImageHeight: rule.exactHeight,
        maxImageWidth: rule.exactWidth,
        maxImageHeight: rule.exactHeight,
        validateMaxWidthHeight: true,
      }
    : {}),
  ...(rule.minWidth ? { minImageWidth: rule.minWidth } : {}),
  ...(rule.maxWidth
    ? { maxImageWidth: rule.maxWidth, maxImageHeight: rule.maxHeight, validateMaxWidthHeight: true }
    : {}),
  styles: { palette: CLOUDINARY_PALETTE },
});

const CATEGORIES = ["announcement", "strategy", "product", "engineering", "policy", "other"];

const STATUS_COLORS = {
  published: "success",
  draft: "secondary",
  unpublished: "warning",
};

const STATUS_DOT = {
  published: "#1a8917",
  draft: "#aaa",
  unpublished: "#d97706",
};

const CATEGORY_ICON = {
  announcement: "📣",
  strategy: "📊",
  product: "🚀",
  engineering: "⚙️",
  policy: "📋",
  other: "📄",
};

/* Mirror of utils/blogSlug.js on the server, for the live URL preview only;
   the server's answer is the one that is stored. */
const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const EMPTY_FORM = {
  title: "",
  // Left blank on a new post so the server derives it from the title; once a
  // post has been published it is frozen and this field is the ONLY way to
  // move it (the old link keeps working, see utils/blogSlug.js).
  slug: "",
  excerpt: "",
  content: "",
  category: "announcement",
  status: "draft",
  author: "Management / Product and Engineering",
  ogImage: "",
  ogDescription: "",
  tags: "",
  version: "",
  preparedFor: "",
  preparedBy: "",
  downloadUrl: "",
  downloadLabel: "",
};

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-UG", {
    year: "numeric", month: "short", day: "numeric",
  });
};

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLoadingId, setEditLoadingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // Whether the post being edited has ever been live: that is the moment its
  // link becomes a promise, and the Link field explains itself accordingly.
  const [editingEverPublished, setEditingEverPublished] = useState(false);
  const [editingPreviousSlugs, setEditingPreviousSlugs] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [hoverCell, setHoverCell] = useState({ r: 0, c: 0 });
  const tablePickerRef = useRef(null);
  const [cloudinaryLoaded, setCloudinaryLoaded] = useState(false);
  const [uploadingOgImage, setUploadingOgImage] = useState(false);
  const styleInjected = useRef(false);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL || "";
  const WEB_URL = SERVER_URL.replace(/\/api$/, "").replace("lusuku-prod-server.herokuapp.com", "lusuku.ug") || "https://lusuku.ug";

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiBlogGetAll();
      setPosts(data.data || []);
    } catch (err) {
      setError("Failed to load posts. " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  useEffect(() => {
    if (window.cloudinary) { setCloudinaryLoaded(true); }
    else {
      const s = document.createElement("script");
      s.src = "https://widget.cloudinary.com/v2.0/global/all.js";
      s.async = true;
      s.onload = () => setCloudinaryLoaded(true);
      document.body.appendChild(s);
    }
  }, []);

  useEffect(() => {
    if (styleInjected.current) return;
    styleInjected.current = true;
    const style = document.createElement("style");
    style.setAttribute("data-blog-manager", "1");
    style.textContent = `
      .blog-manager-modal .modal-dialog { margin: 1.75rem 1rem 1.75rem auto !important; max-width: 1200px !important; }
      .blog-manager-modal .modal-header.dark-header .btn-close {
        filter: invert(1) grayscale(100%) brightness(200%) !important;
        opacity: 0.9 !important;
      }
      .blog-manager-modal .modal-header.dark-header .btn-close:hover { opacity: 1 !important; }
      .tiptap-editor-wrap .ProseMirror { min-height: 460px; padding: 1rem 1.25rem; font-size: 1rem; line-height: 1.75; outline: none; font-family: inherit; }
      .tiptap-editor-wrap .ProseMirror p { margin: 0.35rem 0; }
      .tiptap-editor-wrap .ProseMirror h1 { font-size: 1.7rem; font-weight: 700; margin: 1.5rem 0 0.5rem; }
      .tiptap-editor-wrap .ProseMirror h2 { font-size: 1.35rem; font-weight: 700; margin: 1.25rem 0 0.4rem; }
      .tiptap-editor-wrap .ProseMirror h3 { font-size: 1.1rem; font-weight: 700; margin: 1rem 0 0.35rem; }
      .tiptap-editor-wrap .ProseMirror ul, .tiptap-editor-wrap .ProseMirror ol { padding-left: 1.5rem; margin: 0.5rem 0; }
      .tiptap-editor-wrap .ProseMirror blockquote { border-left: 3px solid #0d2137; margin: 0.75rem 0; padding-left: 1rem; color: #555; }
      .tiptap-editor-wrap .ProseMirror .tableWrapper { overflow-x: auto; margin: 1rem 0; }
      .tiptap-editor-wrap .ProseMirror table { border-collapse: collapse; width: 100%; table-layout: fixed; }
      .tiptap-editor-wrap .ProseMirror table td, .tiptap-editor-wrap .ProseMirror table th { border: 1px solid #ccc; padding: 7px 12px; vertical-align: top; min-width: 60px; position: relative; box-sizing: border-box; }
      .tiptap-editor-wrap .ProseMirror table th { background: #f0f4f8; font-weight: 700; }
      .tiptap-editor-wrap .ProseMirror table .selectedCell:after { content: ""; position: absolute; inset: 0; background: rgba(13,33,55,0.08); pointer-events: none; z-index: 2; }
      .tiptap-editor-wrap .ProseMirror table .column-resize-handle { position: absolute; right: -2px; top: 0; bottom: -2px; width: 4px; background: #0d2137; pointer-events: none; z-index: 20; }
      .tiptap-editor-wrap .ProseMirror.resize-cursor { cursor: col-resize; }
      .tiptap-editor-wrap .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #aaa; pointer-events: none; height: 0; float: left; }
      .tiptap-editor-wrap .ProseMirror mark { border-radius: 2px; padding: 0 2px; }
      .tiptap-editor-wrap .ProseMirror a { color: #0d2137; text-decoration: underline; }
      .tiptap-editor-wrap .ProseMirror div[data-iframe-embed] { margin: 1rem 0; border-radius: 6px; overflow: hidden; }
      .tiptap-editor-wrap .ProseMirror img { max-width: 100%; display: block; border-radius: 4px; margin: 0.5rem 0; }
      input[type="color"] { -webkit-appearance: none; appearance: none; }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.querySelector("style[data-blog-manager]");
      if (el) el.remove();
      styleInjected.current = false;
    };
  }, []);

  const handleNew = () => {
    setEditingId(null);
    setEditingEverPublished(false);
    setEditingPreviousSlugs([]);
    setForm(EMPTY_FORM);
    sessionUploadsRef.current = [];
    loadedAtRef.current = null;
    setShowEditor(true);
  };

  const handleEdit = async (id) => {
    if (editLoadingId) return;

    setEditLoadingId(id);
    setEditingId(id);
    setEditingEverPublished(false);
    setEditingPreviousSlugs([]);
    setForm(EMPTY_FORM);
    sessionUploadsRef.current = [];
    loadedAtRef.current = null;
    setShowEditor(true);

    try {
      const { data } = await apiBlogGetById(id);
      const p = data.data;
      loadedAtRef.current = p.updatedAt || null;
      setEditingEverPublished(Boolean(p.publishedAt) || p.status === "published" || p.status === "unpublished");
      setEditingPreviousSlugs(p.previousSlugs || []);
      setForm({
        title: p.title || "",
        slug: p.slug || "",
        excerpt: p.excerpt || "",
        content: p.content || "",
        category: p.category || "announcement",
        status: p.status || "draft",
        author: p.author || "",
        ogImage: p.ogImage || "",
        ogDescription: p.ogDescription || "",
        tags: (p.tags || []).join(", "),
        version: p.version || "",
        preparedFor: p.preparedFor || "",
        preparedBy: p.preparedBy || "",
        downloadUrl: p.downloadUrl || "",
        downloadLabel: p.downloadLabel || "",
      });
    } catch (err) {
      setShowEditor(false);
      setEditingId(null);
      toast.error("Could not load post for editing.");
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.warn("Title and content are required.");
      return;
    }
    setSaving(true);
    // Hand the session list to the save and clear it now, before the request
    // goes out: a Cancel clicked while this is in flight must not be able to
    // ask the server to destroy the very asset this save is attaching. Put
    // back on failure so a retry still sweeps.
    const uploads = sessionUploadsRef.current;
    sessionUploadsRef.current = [];
    try {
      const payload = {
        ...form,
        tags: form.tags
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
        // Not post fields. sessionUploads is what the server sweeps once the
        // post is written, which is how a replaced cover image or a deleted
        // body image leaves Cloudinary too; ifUnmodifiedSince makes the
        // server refuse a save built on a copy someone else has since edited.
        sessionUploads: uploads,
        ifUnmodifiedSince: loadedAtRef.current,
      };
      if (editingId) {
        await apiBlogUpdate(editingId, payload);
      } else {
        await apiBlogCreate(payload);
      }
      setShowEditor(false);
      await loadPosts();
    } catch (err) {
      sessionUploadsRef.current = uploads;
      toast.error("Save failed: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (id) => {
    setActionLoading(id + "_publish");
    try {
      await apiBlogPublish(id);
      await loadPosts();
    } catch (err) {
      toast.error("Failed to publish: " + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpublish = async (id) => {
    setActionLoading(id + "_unpublish");
    try {
      await apiBlogUnpublish(id);
      await loadPosts();
    } catch (err) {
      toast.error("Failed to unpublish: " + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  /* Assets uploaded during THIS editing session. An upload lands on Cloudinary
     the moment it is picked, so anything the operator then removes, replaces or
     abandons has to be taken back off storage. The list rides along with the
     save (the server sweeps it once the post is written) and is swept directly
     when the editor is closed without saving. */
  const sessionUploadsRef = useRef([]);

  /* `updatedAt` of the post as it was loaded into this editor, sent back on
     save so the server can refuse a write built on a stale copy. */
  const loadedAtRef = useRef(null);

  const discardMedia = useCallback(async (urls) => {
    const list = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
    if (list.length === 0) return;
    try {
      await apiBlogDiscardMedia(list);
    } catch (err) {
      // Storage cleanup must never block editorial work; the server also
      // sweeps on the next save of this post.
      console.warn("[BlogManager] media discard failed:", err?.response?.data?.message || err.message);
    }
  }, []);

  /**
   * One uploader for all three roles. `onDone(url, info)` is called with the
   * delivery URL on success and with null when the operator closed the widget
   * or the file failed the gate.
   */
  const openUploader = useCallback((ruleKey, onDone) => {
    const rule = BLOG_MEDIA_RULES[ruleKey];
    if (!cloudinaryLoaded || !window.cloudinary) {
      toast.warn("The uploader is still loading, please try again in a moment.");
      return null;
    }
    const widget = window.cloudinary.createUploadWidget(
      widgetOptionsFor(rule),
      (error, result) => {
        if (error) { onDone(null, { error }); return; }
        if (result?.event === "close") { onDone(null, { closed: true }); return; }
        if (result?.event !== "success") return;

        const info = result.info || {};
        const problem = dimensionError(rule, Number(info.width), Number(info.height));
        if (problem) {
          // The asset is already on Cloudinary. Take it straight back off
          // rather than leaving a rejected file sitting in the account.
          discardMedia(info.secure_url);
          toast.warn(problem);
          onDone(null, { rejected: true });
          return;
        }

        const url = rule.transform && typeof info.secure_url === "string"
          ? info.secure_url.replace("/upload/", `/upload/${rule.transform}/`)
          : info.secure_url;
        sessionUploadsRef.current = [...sessionUploadsRef.current, url];
        onDone(url, info);
        widget.close();
      }
    );
    widget.open();
    return widget;
  }, [cloudinaryLoaded, discardMedia]);

  const handleAttachmentUpload = () => {
    openUploader("attachment", (url, info) => {
      if (!url) return;
      const ext = info?.format ? String(info.format).toUpperCase() : "";
      const name = info?.original_filename
        ? info.original_filename.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : "Download";
      setForm((prev) => ({
        ...prev,
        downloadUrl: url,
        downloadLabel: prev.downloadLabel || (ext ? `${name} (${ext})` : name),
      }));
    });
  };

  const handleOgImageUpload = () => {
    setUploadingOgImage(true);
    const widget = openUploader("ogImage", (url) => {
      setUploadingOgImage(false);
      if (url) handleFormChange("ogImage", url);
    });
    if (!widget) setUploadingOgImage(false);
  };

  const handleInlineImageUpload = () => {
    openUploader("inlineImage", (url) => {
      if (!url) return;
      tiptapEditor?.chain().focus().setImage({ src: url, width: "100%", align: "center" }).run();
    });
  };

  /* Closing without saving. Everything uploaded in this session is offered to
     the sweep; the server keeps any URL a post still references, so reopening
     an article and cancelling never strips its live images. */
  const closeEditor = useCallback(() => {
    if (saving) return;
    const pending = sessionUploadsRef.current;
    sessionUploadsRef.current = [];
    setShowEditor(false);
    discardMedia(pending);
  }, [discardMedia, saving]);

  const handleShare = (platform, post) => {
    const url = encodeURIComponent(`${WEB_URL}/blog/${post.slug}`);
    const title = encodeURIComponent(post.title);
    if (platform === "linkedin") {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "_blank");
    } else if (platform === "whatsapp") {
      window.open(`https://wa.me/?text=${title}%20${url}`, "_blank");
    } else if (platform === "copy") {
      navigator.clipboard.writeText(`${WEB_URL}/blog/${post.slug}`);
      setCopiedId(post._id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const tiptapEditor = useEditor({
    extensions: [
      StarterKit.configure({ image: false }),
      Underline,
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      ResizableImage.configure({ inline: false, allowBase64: true }),
      IframeEmbed,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder: "Tell your story…" }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            backgroundColor: {
              default: null,
              parseHTML: (el) => el.style.backgroundColor || null,
              renderHTML: (attrs) => {
                if (!attrs.backgroundColor) return {};
                return { style: `background-color: ${attrs.backgroundColor}` };
              },
            },
          };
        },
      }),
    ],
    content: form.content || "",
    onUpdate: ({ editor }) => handleFormChange("content", editor.getHTML()),
  });

  useEffect(() => {
    if (!tiptapEditor) return;
    const current = tiptapEditor.getHTML();
    if (current !== form.content) {
      tiptapEditor.commands.setContent(form.content || "", false);
    }
  }, [form.content, tiptapEditor]);

  useEffect(() => {
    if (!showTablePicker) return;
    const handler = (e) => {
      if (tablePickerRef.current && !tablePickerRef.current.contains(e.target)) {
        setShowTablePicker(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showTablePicker]);

  const insertTable = (rows, cols) => {
    if (!tiptapEditor) return;
    tiptapEditor.chain().focus()
      .insertTable({ rows, cols, withHeaderRow: true })
      .run();
    setShowTablePicker(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setActionLoading(deleteTarget + "_delete");
    try {
      await apiBlogDelete(deleteTarget);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await loadPosts();
    } catch (err) {
      toast.error("Delete failed: " + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "3rem 0" }}>
        {[1, 2, 3].map((n) => (
          <div key={n} style={styles.skeletonRow}>
            <div style={{ ...styles.skeletonBlock, width: 48, height: 48, borderRadius: "50%", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ ...styles.skeletonBlock, width: "55%", height: 14, marginBottom: 8 }} />
              <div style={{ ...styles.skeletonBlock, width: "80%", height: 20, marginBottom: 8 }} />
              <div style={{ ...styles.skeletonBlock, width: "70%", height: 13 }} />
            </div>
            <div style={{ ...styles.skeletonBlock, width: 80, height: 56, borderRadius: 6 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={styles.page} className="blog-manager-page">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={styles.header}>
        <div>
          <h3 style={styles.headerTitle}>Insights & Blog</h3>
          <p style={styles.headerSub}>
            Board papers, strategy notes, and official communications
          </p>
        </div>
        <button style={styles.newBtn} onClick={handleNew}>
          Write a story
        </button>
      </div>

      {error && (
        <div style={styles.errorBanner}>{error}</div>
      )}

      {/* ── Empty state ─────────────────────────────────────────────────── */}
      {posts.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: "5rem", marginBottom: 16 }}>📝</div>
          <p style={styles.emptyTitle}>Start publishing</p>
          <p style={styles.emptySub}>Your stories will appear here once you create them.</p>
          <button style={styles.newBtn} onClick={handleNew}>Write a story</button>
        </div>
      ) : (
        <div style={styles.list}>
          {posts.map((post, idx) => (
            <div key={post._id} style={{
              ...styles.storyRow,
              borderTop: idx === 0 ? "none" : "1px solid #e8e8e8",
            }}>
              {/* Left — avatar + meta + title + actions */}
              <div style={styles.storyLeft}>
                {/* Author row */}
                <div style={styles.authorRow}>
                  <div style={styles.avatar}>
                    {CATEGORY_ICON[post.category] || "📄"}
                  </div>
                  <div>
                    <span style={styles.authorName}>{post.author || "Lusuku Team"}</span>
                    <span style={styles.authorDot}>·</span>
                    <span style={styles.authorDate}>
                      {post.status === "published"
                        ? formatDate(post.publishedAt)
                        : `Draft — ${formatDate(post.createdAt)}`}
                    </span>
                  </div>
                </div>

                {/* Title & excerpt */}
                <h2 style={styles.storyTitle} onClick={() => !editLoadingId && handleEdit(post._id)}>
                  {post.title}
                </h2>
                {post.excerpt && (
                  <p style={styles.storyExcerpt}>{post.excerpt}</p>
                )}

                {/* Bottom meta row */}
                <div style={styles.metaRow}>
                  {/* Status pill */}
                  <span style={{
                    ...styles.statusPill,
                    background: post.status === "published" ? "#e6f4ea" : post.status === "draft" ? "#f5f5f5" : "#fff7e6",
                    color: STATUS_DOT[post.status] || "#aaa",
                  }}>
                    <span style={{
                      display: "inline-block", width: 7, height: 7, borderRadius: "50%",
                      background: STATUS_DOT[post.status] || "#aaa", marginRight: 5,
                    }} />
                    {post.status}
                  </span>

                  {/* Category pill */}
                  <span style={styles.catPill}>
                    {post.category}
                  </span>

                  {/* Views */}
                  <span style={styles.metaMuted}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: "middle", marginRight: 3 }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    {(post.viewCount || 0).toLocaleString()} views
                  </span>

                  {/* Download indicator */}
                  {post.downloadUrl && (
                    <span style={styles.metaMuted} title="Has download attached">
                      ⬇ download
                    </span>
                  )}

                  {/* Version */}
                  {post.version && (
                    <span style={styles.metaMuted}>v{post.version}</span>
                  )}

                  {/* Action links */}
                  <span style={styles.actionsDivider} />

                  <button
                    style={{
                      ...styles.textBtn,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      minWidth: editLoadingId === post._id ? 76 : undefined,
                      opacity: editLoadingId && editLoadingId !== post._id ? 0.5 : 1,
                      cursor: editLoadingId ? "wait" : "pointer",
                    }}
                    onClick={() => handleEdit(post._id)}
                    disabled={Boolean(editLoadingId)}
                    aria-busy={editLoadingId === post._id}
                  >
                    {editLoadingId === post._id ? (
                      <>
                        <Spinner as="span" size="sm" animation="border" style={{ width: 12, height: 12 }} />
                        Loading…
                      </>
                    ) : "Edit"}
                  </button>

                  {post.status !== "published" && (
                    <button
                      style={{ ...styles.textBtn, color: "#1a8917" }}
                      disabled={actionLoading === post._id + "_publish"}
                      onClick={() => handlePublish(post._id)}
                    >
                      {actionLoading === post._id + "_publish"
                        ? <Spinner as="span" size="sm" animation="border" style={{ width: 12, height: 12 }} />
                        : "Publish"}
                    </button>
                  )}

                  {post.status === "published" && (
                    <>
                      <button
                        style={{ ...styles.textBtn, color: "#d97706" }}
                        disabled={actionLoading === post._id + "_unpublish"}
                        onClick={() => handleUnpublish(post._id)}
                      >
                        {actionLoading === post._id + "_unpublish"
                          ? <Spinner as="span" size="sm" animation="border" style={{ width: 12, height: 12 }} />
                          : "Unpublish"}
                      </button>
                      <button
                        style={{ ...styles.textBtn, color: "#1a8917" }}
                        onClick={() => window.open(`${WEB_URL}/blog/${post.slug}`, "_blank")}
                      >
                        View live ↗
                      </button>
                      {/* ── Share buttons for published posts ── */}
                      <span style={styles.actionsDivider} />
                      <span style={{ ...styles.metaMuted, marginRight: 2 }}>Share:</span>
                      <button
                        style={{ ...styles.textBtn, color: "#0077b5" }}
                        title="Share on LinkedIn"
                        onClick={() => handleShare("linkedin", post)}
                      >
                        in
                      </button>
                      <button
                        style={{ ...styles.textBtn, color: "#25d366", display: "inline-flex", alignItems: "center", gap: 4 }}
                        title="Share on WhatsApp"
                        onClick={() => handleShare("whatsapp", post)}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                      </button>
                      <button
                        style={{ ...styles.textBtn, color: copiedId === post._id ? "#1a8917" : "#6b6b6b", display: "inline-flex", alignItems: "center", gap: 4, transition: "color 0.2s" }}
                        title="Copy link"
                        onClick={() => handleShare("copy", post)}
                      >
                        {copiedId === post._id ? (
                          <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Copied!</>
                        ) : (
                          <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg> Copy link</>
                        )}
                      </button>
                    </>
                  )}

                  <button
                    style={{ ...styles.textBtn, color: "#c62828" }}
                    onClick={() => { setDeleteTarget(post._id); setShowDeleteModal(true); }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Right — thumbnail / icon block */}
              <div style={styles.storyThumb}>
                {post.ogImage
                  ? <img src={post.ogImage} alt="" style={styles.thumbImg} />
                  : <div style={styles.thumbIcon}>{CATEGORY_ICON[post.category] || "📄"}</div>
                }
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Post Editor Modal ─────────────────────────────────────────────── */}
      <Modal show={showEditor} onHide={closeEditor} size="xl" backdrop="static" dialogClassName="blog-manager-modal">
        <Modal.Header closeButton style={styles.modalHeader} className="dark-header">
          <Modal.Title style={styles.modalTitle}>
            {editingId ? "Edit story" : "New story"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "82vh", overflowY: "auto", padding: "1.75rem 2rem" }}>
          {editLoadingId ? (
            <div style={styles.editorLoading} role="status" aria-live="polite">
              <Spinner animation="border" style={{ width: 34, height: 34, color: "#1a8917" }} />
              <div style={styles.editorLoadingTitle}>Loading story…</div>
              <div style={styles.editorLoadingText}>Preparing the article and editor.</div>
            </div>
          ) : (
          <Row>
            {/* ── Main content column ── */}
            <Col md={8}>
              <Form.Group className="mb-4">
                <Form.Control
                  type="text"
                  value={form.title}
                  onChange={(e) => handleFormChange("title", e.target.value)}
                  placeholder="Title"
                  style={styles.titleInput}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={form.excerpt}
                  onChange={(e) => handleFormChange("excerpt", e.target.value)}
                  placeholder="Write a subtitle or summary…"
                  style={styles.subtitleInput}
                  maxLength={300}
                />
              </Form.Group>

              <Form.Group className="mb-4" style={{ position: "relative", display: "flex", flexDirection: "column" }}>
                {/* ── TipTap Toolbar ── */}
                <div style={styles.tiptapToolbar}>
                  {/* Font family */}
                  <AppSelect
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.preventDefault();
                      if (e.target.value === "") tiptapEditor?.chain().focus().unsetFontFamily().run();
                      else tiptapEditor?.chain().focus().setFontFamily(e.target.value).run();
                    }}
                    style={styles.tbSelect}
                    defaultValue=""
                  >
                    <option value="">Default</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Georgia', serif">Georgia</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                  </AppSelect>
                  {/* Font size */}
                  <AppSelect
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.preventDefault();
                      if (e.target.value === "") tiptapEditor?.chain().focus().unsetFontSize().run();
                      else tiptapEditor?.chain().focus().setFontSize(e.target.value).run();
                    }}
                    style={styles.tbSelect}
                    defaultValue=""
                  >
                    <option value="">Size</option>
                    {["10px","12px","14px","16px","18px","20px","24px","28px","32px","36px","48px"].map(s => (
                      <option key={s} value={s}>{s.replace("px","pt")}</option>
                    ))}
                  </AppSelect>
                  <span style={styles.tbDivider} />
                  {/* Headings */}
                  {[1,2,3].map(level => (
                    <button key={level} type="button"
                      onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus().toggleHeading({ level }).run(); }}
                      style={{ ...styles.tbBtn, fontWeight: tiptapEditor?.isActive("heading", { level }) ? 700 : 400, background: tiptapEditor?.isActive("heading", { level }) ? "#e3ecf5" : "transparent" }}>
                      H{level}
                    </button>
                  ))}
                  <span style={styles.tbDivider} />
                  {[
                    { label: "B", cmd: "toggleBold", active: "bold", style: { fontWeight: 700 } },
                    { label: "I", cmd: "toggleItalic", active: "italic", style: { fontStyle: "italic" } },
                    { label: "U", cmd: "toggleUnderline", active: "underline", style: { textDecoration: "underline" } },
                    { label: "S", cmd: "toggleStrike", active: "strike", style: { textDecoration: "line-through" } },
                  ].map(({ label, cmd, active, style: s }) => (
                    <button key={label} type="button"
                      onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus()[cmd]().run(); }}
                      style={{ ...styles.tbBtn, ...s, background: tiptapEditor?.isActive(active) ? "#e3ecf5" : "transparent" }}>
                      {label}
                    </button>
                  ))}
                  <span style={styles.tbDivider} />
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus().toggleBulletList().run(); }}
                    style={{ ...styles.tbBtn, background: tiptapEditor?.isActive("bulletList") ? "#e3ecf5" : "transparent" }}>≡</button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus().toggleOrderedList().run(); }}
                    style={{ ...styles.tbBtn, background: tiptapEditor?.isActive("orderedList") ? "#e3ecf5" : "transparent" }}>1.</button>
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus().toggleBlockquote().run(); }}
                    style={{ ...styles.tbBtn, background: tiptapEditor?.isActive("blockquote") ? "#e3ecf5" : "transparent" }}>❝</button>
                  <span style={styles.tbDivider} />
                  {/* Alignment */}
                  <button type="button"
                    onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus().setTextAlign("left").run(); }}
                    style={{ ...styles.tbBtn, background: tiptapEditor?.isActive({ textAlign: "left" }) ? "#e3ecf5" : "transparent" }}
                    title="Align left">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>
                  </button>
                  <button type="button"
                    onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus().setTextAlign("center").run(); }}
                    style={{ ...styles.tbBtn, background: tiptapEditor?.isActive({ textAlign: "center" }) ? "#e3ecf5" : "transparent" }}
                    title="Align center">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                  </button>
                  <button type="button"
                    onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus().setTextAlign("right").run(); }}
                    style={{ ...styles.tbBtn, background: tiptapEditor?.isActive({ textAlign: "right" }) ? "#e3ecf5" : "transparent" }}
                    title="Align right">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>
                  </button>
                  <button type="button"
                    onMouseDown={(e) => { e.preventDefault(); tiptapEditor?.chain().focus().setTextAlign("justify").run(); }}
                    style={{ ...styles.tbBtn, background: tiptapEditor?.isActive({ textAlign: "justify" }) ? "#e3ecf5" : "transparent" }}
                    title="Justify">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                  </button>
                  <span style={styles.tbDivider} />
                  {/* Text color */}
                  <label style={{ ...styles.tbBtn, padding: "2px 4px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }} title="Text color">
                    <span style={{ fontSize: "0.78rem", fontWeight: 700, lineHeight: 1 }}>A</span>
                    <input type="color" defaultValue="#000000"
                      onBlur={(e) => tiptapEditor?.chain().focus().setColor(e.target.value).run()}
                      onChange={(e) => tiptapEditor?.chain().focus().setColor(e.target.value).run()}
                      style={{ width: 18, height: 6, padding: 0, border: "none", cursor: "pointer", borderRadius: 2 }}
                    />
                  </label>
                  {/* Highlight color */}
                  <label style={{ ...styles.tbBtn, padding: "2px 4px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }} title="Highlight">
                    <span style={{ fontSize: "0.78rem", lineHeight: 1 }}>H</span>
                    <input type="color" defaultValue="#ffff00"
                      onBlur={(e) => tiptapEditor?.chain().focus().setHighlight({ color: e.target.value }).run()}
                      onChange={(e) => tiptapEditor?.chain().focus().setHighlight({ color: e.target.value }).run()}
                      style={{ width: 18, height: 6, padding: 0, border: "none", cursor: "pointer", borderRadius: 2 }}
                    />
                  </label>
                  <span style={styles.tbDivider} />
                  {/* Link */}
                  <button type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (tiptapEditor?.isActive("link")) { tiptapEditor.chain().focus().unsetLink().run(); return; }
                      const url = window.prompt("Enter URL:");
                      if (url) tiptapEditor?.chain().focus().setLink({ href: url, target: "_blank" }).run();
                    }}
                    style={{ ...styles.tbBtn, background: tiptapEditor?.isActive("link") ? "#e3ecf5" : "transparent", textDecoration: "underline", color: tiptapEditor?.isActive("link") ? "#0d2137" : "#555" }}
                    title="Insert / remove link">
                    🔗
                  </button>
                  {/* Insert image (upload) */}
                  <button type="button"
                    onMouseDown={(e) => { e.preventDefault(); handleInlineImageUpload(); }}
                    style={{ ...styles.tbBtn }}
                    title="Insert image"
                    disabled={!cloudinaryLoaded}>
                    🖼
                  </button>
                  {/* YouTube / video embed */}
                  <button type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const raw = window.prompt("YouTube or video URL:");
                      if (!raw) return;
                      let src = raw.trim();
                      const ytMatch = src.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([-\w]+)/);
                      if (ytMatch) src = `https://www.youtube.com/embed/${ytMatch[1]}`;
                      tiptapEditor?.chain().focus().insertIframe({ src }).run();
                    }}
                    style={{ ...styles.tbBtn }} title="Embed YouTube / video">
                    ▶
                  </button>
                  <span style={styles.tbDivider} />
                  {/* Table insert with grid picker */}
                  <div style={{ position: "relative" }} ref={tablePickerRef}>
                    <button type="button"
                      onMouseDown={(e) => { e.preventDefault(); setShowTablePicker((v) => !v); }}
                      style={{ ...styles.tbBtn, background: showTablePicker ? "#e3ecf5" : "transparent" }}>
                      ⊞ Table
                    </button>
                    {showTablePicker && (
                      <div style={styles.tablePickerDropdown} onMouseLeave={() => setHoverCell({ r: 0, c: 0 })}>
                        <div style={{ fontSize: "0.72rem", color: "#888", marginBottom: 6, textAlign: "center" }}>
                          {hoverCell.r > 0 && hoverCell.c > 0 ? `${hoverCell.r} × ${hoverCell.c}` : "Select size"}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 20px)", gap: 2 }}>
                          {Array.from({ length: 6 }, (_, r) =>
                            Array.from({ length: 8 }, (_, c) => {
                              const active = r < hoverCell.r && c < hoverCell.c;
                              return (
                                <div key={`${r}-${c}`}
                                  onMouseEnter={() => setHoverCell({ r: r + 1, c: c + 1 })}
                                  onMouseDown={(e) => { e.preventDefault(); insertTable(r + 1, c + 1); }}
                                  style={{ width: 20, height: 20, borderRadius: 2, cursor: "pointer", border: `1.5px solid ${active ? "#0d2137" : "#ccc"}`, background: active ? "#e3ecf5" : "#fafafa" }}
                                />
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Table controls (only shown when cursor is in table) */}
                  {tiptapEditor?.isActive("table") && (
                    <>
                      <span style={styles.tbDivider} />
                      <button type="button" onMouseDown={(e) => { e.preventDefault(); tiptapEditor.chain().focus().addColumnAfter().run(); }} style={styles.tbBtn} title="Add column">+col</button>
                      <button type="button" onMouseDown={(e) => { e.preventDefault(); tiptapEditor.chain().focus().addRowAfter().run(); }} style={styles.tbBtn} title="Add row">+row</button>
                      <button type="button" onMouseDown={(e) => { e.preventDefault(); tiptapEditor.chain().focus().deleteColumn().run(); }} style={{ ...styles.tbBtn, color: "#c62828" }} title="Delete column">-col</button>
                      <button type="button" onMouseDown={(e) => { e.preventDefault(); tiptapEditor.chain().focus().deleteRow().run(); }} style={{ ...styles.tbBtn, color: "#c62828" }} title="Delete row">-row</button>
                      <button type="button" onMouseDown={(e) => { e.preventDefault(); tiptapEditor.chain().focus().deleteTable().run(); }} style={{ ...styles.tbBtn, color: "#c62828" }} title="Delete table">✕ table</button>
                      <span style={styles.tbDivider} />
                      {/* Cell background color */}
                      <label style={{ ...styles.tbBtn, padding: "2px 4px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }} title="Cell background color">
                        <span style={{ fontSize: "0.68rem", lineHeight: 1, color: "#555" }}>cell</span>
                        <input type="color" defaultValue="#ffffff"
                          onBlur={(e) => tiptapEditor?.chain().focus().setCellAttribute("backgroundColor", e.target.value).run()}
                          onChange={(e) => tiptapEditor?.chain().focus().setCellAttribute("backgroundColor", e.target.value).run()}
                          style={{ width: 18, height: 6, padding: 0, border: "none", cursor: "pointer", borderRadius: 2 }}
                        />
                      </label>
                    </>
                  )}
                </div>

                {/* ── TipTap Editor ── */}
                <div style={styles.tiptapScrollWrap}>
                  <div style={styles.tiptapEditorWrap} className="tiptap-editor-wrap">
                    <EditorContent editor={tiptapEditor} />
                  </div>
                </div>
              </Form.Group>
            </Col>

            {/* ── Settings column ── */}
            <Col md={4}>
              <div style={styles.settingsPanel}>
                <p style={styles.settingsSectionLabel}>Publishing</p>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>Status</Form.Label>
                  <AppSelect block value={form.status} onChange={(e) => handleFormChange("status", e.target.value)} style={styles.select}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="unpublished">Unpublished</option>
                  </AppSelect>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>
                    Link{" "}
                    <small style={{ color: "#aaa" }}>
                      {editingEverPublished ? "(fixed once published)" : "(follows the title until published)"}
                    </small>
                  </Form.Label>
                  <div style={styles.slugRow}>
                    <span style={styles.slugPrefix}>{WEB_URL.replace(/^https?:\/\//, "")}/blog/</span>
                    <Form.Control
                      type="text"
                      value={form.slug}
                      onChange={(e) => handleFormChange("slug", e.target.value)}
                      onBlur={(e) => handleFormChange("slug", slugify(e.target.value))}
                      placeholder={slugify(form.title) || "from-the-title"}
                      style={{ ...styles.input, ...styles.slugInput }}
                      spellCheck={false}
                    />
                  </div>
                  <div style={styles.slugHint}>
                    {editingEverPublished
                      ? "Editing the title no longer changes this link. If you change it here on purpose, every link already shared keeps working and sends readers to the new address."
                      : "Leave it blank and the link is made from the title. Set your own to fix it now."}
                  </div>
                  {editingPreviousSlugs.length > 0 && (
                    <div style={styles.slugHistory}>
                      Also reachable at:{" "}
                      {editingPreviousSlugs.map((s) => (
                        <code key={s} style={styles.slugHistoryItem}>/blog/{s}</code>
                      ))}
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>Category</Form.Label>
                  <AppSelect block value={form.category} onChange={(e) => handleFormChange("category", e.target.value)} style={styles.select}>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                    ))}
                  </AppSelect>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>Tags <small style={{ color: "#aaa" }}>(comma-separated)</small></Form.Label>
                  <Form.Control
                    type="text"
                    value={form.tags}
                    onChange={(e) => handleFormChange("tags", e.target.value)}
                    placeholder="delivery, pricing, strategy"
                    style={styles.input}
                  />
                </Form.Group>

                <hr style={{ borderColor: "#e8e8e8", margin: "1.25rem 0" }} />
                <p style={styles.settingsSectionLabel}>Authorship</p>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>Author</Form.Label>
                  <Form.Control type="text" value={form.author} onChange={(e) => handleFormChange("author", e.target.value)} style={styles.input} />
                </Form.Group>

                <Row className="mb-3">
                  <Col>
                    <Form.Label style={styles.fieldLabel}>Version</Form.Label>
                    <Form.Control type="text" value={form.version} onChange={(e) => handleFormChange("version", e.target.value)} placeholder="2.0" style={styles.input} />
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>Prepared For</Form.Label>
                  <Form.Control type="text" value={form.preparedFor} onChange={(e) => handleFormChange("preparedFor", e.target.value)} placeholder="Lusuku Board" style={styles.input} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>Prepared By</Form.Label>
                  <Form.Control type="text" value={form.preparedBy} onChange={(e) => handleFormChange("preparedBy", e.target.value)} placeholder="Management / Product" style={styles.input} />
                </Form.Group>

                <hr style={{ borderColor: "#e8e8e8", margin: "1.25rem 0" }} />
                <p style={styles.settingsSectionLabel}>Download / Attachment</p>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>Attachment file</Form.Label>

                  {form.downloadUrl ? (
                    <div style={styles.attachmentPreview}>
                      <span style={{ fontSize: "1.4rem" }}>📎</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {form.downloadLabel || "Attached file"}
                        </div>
                        <a
                          href={form.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: "0.73rem", color: "#1a8917", wordBreak: "break-all" }}
                        >
                          View file ↗
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={() => { handleFormChange("downloadUrl", ""); handleFormChange("downloadLabel", ""); }}
                        style={styles.removeAttachBtn}
                      >
                        × Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleAttachmentUpload}
                      disabled={!cloudinaryLoaded}
                      style={styles.uploadAttachBtn}
                    >
                      📄 Upload PDF / DOCX / XLSX…
                    </button>
                  )}
                  <Form.Text style={{ color: "#aaa", fontSize: "0.78rem", display: "block", marginTop: 4 }}>
                    File is hosted on Cloudinary. A “Download” button appears on the article automatically.
                  </Form.Text>
                </Form.Group>

                {/* Editable label override */}
                {form.downloadUrl && (
                  <Form.Group className="mb-3">
                    <Form.Label style={styles.fieldLabel}>Button label <small style={{ color: "#aaa" }}>(auto-set, edit if needed)</small></Form.Label>
                    <Form.Control
                      type="text"
                      value={form.downloadLabel}
                      onChange={(e) => handleFormChange("downloadLabel", e.target.value)}
                      placeholder="Download PDF · Board Paper v2.0"
                      style={styles.input}
                    />
                  </Form.Group>
                )}

                <hr style={{ borderColor: "#e8e8e8", margin: "1.25rem 0" }} />
                <p style={styles.settingsSectionLabel}>SEO / Social Preview</p>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>OG Image <small style={{ color: "#aaa" }}>(1200×630px)</small></Form.Label>
                  {form.ogImage ? (
                    <div style={{ position: "relative", marginBottom: 8 }}>
                      <img
                        src={form.ogImage}
                        alt="OG preview"
                        style={{ width: "100%", borderRadius: 6, display: "block", border: "1px solid #e0e0e0" }}
                      />
                      <button
                        type="button"
                        onClick={() => handleFormChange("ogImage", "")}
                        style={{
                          position: "absolute", top: 6, right: 6,
                          background: "rgba(0,0,0,0.55)", color: "#fff",
                          border: "none", borderRadius: 4, padding: "2px 8px",
                          fontSize: "0.75rem", cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleOgImageUpload}
                    disabled={uploadingOgImage || !cloudinaryLoaded}
                    style={{
                      width: "100%", padding: "8px 12px",
                      border: "1px dashed #bbb", borderRadius: 6,
                      background: "#fafafa", cursor: "pointer",
                      fontSize: "0.82rem", color: "#555", fontWeight: 500,
                    }}
                  >
                    {uploadingOgImage ? "Uploading…" : form.ogImage ? "Change image" : "📷  Upload cover image"}
                  </button>
                  <Form.Text style={{ color: "#aaa", fontSize: "0.78rem", display: "block", marginTop: 4 }}>
                    Must be exactly 1200 x 630 px, under 5 MB. Any other size is rejected.
                    Removing or replacing the cover deletes the old file from Cloudinary when you save.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={styles.fieldLabel}>OG Description <small style={{ color: "#aaa" }}>(max 160)</small></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.ogDescription}
                    onChange={(e) => handleFormChange("ogDescription", e.target.value)}
                    placeholder="Social preview description…"
                    maxLength={160}
                    style={styles.input}
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
          )}
        </Modal.Body>
        {!editLoadingId && (
          <Modal.Footer style={{ borderTop: "1px solid #e8e8e8" }}>
            <button style={styles.cancelBtn} onClick={closeEditor} disabled={saving}>Cancel</button>
            <button style={styles.publishBtn} onClick={handleSave} disabled={saving}>
              {saving ? <><Spinner as="span" size="sm" animation="border" style={{ width: 14, height: 14, marginRight: 6 }} />Saving…</> : "Save story"}
            </button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ── Delete Confirm Modal ──────────────────────────────────────────── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #e8e8e8" }}>
          <Modal.Title>Delete story</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.95rem", color: "#444" }}>
          This will permanently remove the post from the platform. This action cannot be undone.
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #e8e8e8" }}>
          <button style={styles.cancelBtn} onClick={() => setShowDeleteModal(false)}>Cancel</button>
          <button
            style={{ ...styles.publishBtn, background: "#c62828" }}
            onClick={handleDeleteConfirm}
            disabled={actionLoading === deleteTarget + "_delete"}
          >
            {actionLoading === deleteTarget + "_delete"
              ? <Spinner as="span" size="sm" animation="border" style={{ width: 14, height: 14 }} />
              : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const styles = {
  page: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "2rem 1.5rem 4rem",
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  modalHeader: {
    background: "#0d2137",
    borderBottom: "none",
    padding: "1.25rem 1.75rem",
  },
  modalTitle: {
    fontFamily: "'Georgia', serif",
    fontSize: "1.25rem",
    color: "#ffffff",
  },
  editorLoading: {
    minHeight: 420,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#191919",
  },
  editorLoadingTitle: {
    marginTop: 16,
    fontSize: "1rem",
    fontWeight: 700,
  },
  editorLoadingText: {
    marginTop: 4,
    fontSize: "0.84rem",
    color: "#777",
  },
  tabRow: {
    display: "flex",
    alignItems: "center",
    borderBottom: "2px solid #e8e8e8",
    marginBottom: "0.75rem",
    gap: 0,
  },
  tab: {
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    marginBottom: -2,
    padding: "6px 16px",
    fontSize: "0.88rem",
    fontWeight: 600,
    color: "#aaa",
    cursor: "pointer",
  },
  tabActive: {
    color: "#191919",
    borderBottomColor: "#191919",
  },
  previewPanel: {
    minHeight: 520,
    border: "1px solid #e8e8e8",
    borderRadius: 6,
    padding: "1.25rem 1.5rem",
    fontSize: "0.95rem",
    fontFamily: "'Georgia', serif",
    lineHeight: 1.85,
    color: "#191919",
    overflowY: "auto",
    background: "#fafafa",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #e8e8e8",
    paddingBottom: "1.5rem",
    marginBottom: "2rem",
  },
  headerTitle: {
    fontFamily: "'Georgia', serif",
    fontSize: "1.6rem",
    fontWeight: 700,
    color: "#191919",
    margin: 0,
  },
  headerSub: {
    fontSize: "0.88rem",
    color: "#6b6b6b",
    margin: "4px 0 0",
  },
  newBtn: {
    background: "#1a8917",
    color: "white",
    border: "none",
    borderRadius: 99,
    padding: "9px 22px",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.2s",
  },
  errorBanner: {
    background: "#fce4ec",
    color: "#c62828",
    borderRadius: 8,
    padding: "0.75rem 1rem",
    marginBottom: "1.5rem",
    fontSize: "0.88rem",
  },
  list: {
    display: "flex",
    flexDirection: "column",
  },
  storyRow: {
    display: "flex",
    gap: "1.5rem",
    padding: "2rem 0",
    alignItems: "flex-start",
  },
  storyLeft: {
    flex: 1,
    minWidth: 0,
  },
  authorRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#f0f0f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    flexShrink: 0,
  },
  authorName: {
    fontSize: "0.82rem",
    fontWeight: 500,
    color: "#191919",
  },
  authorDot: {
    color: "#999",
    margin: "0 4px",
    fontSize: "0.9rem",
  },
  authorDate: {
    fontSize: "0.78rem",
    color: "#999",
  },
  storyTitle: {
    fontFamily: "'Georgia', serif",
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "#191919",
    lineHeight: 1.4,
    margin: "0 0 6px",
    cursor: "pointer",
  },
  storyExcerpt: {
    fontSize: "0.88rem",
    color: "#6b6b6b",
    lineHeight: 1.6,
    margin: "0 0 14px",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.72rem",
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 20,
    textTransform: "capitalize",
  },
  catPill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.72rem",
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 20,
    background: "#f0f4ff",
    color: "#1565c0",
    textTransform: "capitalize",
  },
  metaMuted: {
    fontSize: "0.78rem",
    color: "#aaa",
  },
  actionsDivider: {
    display: "inline-block",
    width: 1,
    height: 14,
    background: "#e0e0e0",
    margin: "0 2px",
  },
  textBtn: {
    background: "none",
    border: "none",
    padding: "2px 4px",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#6b6b6b",
    cursor: "pointer",
  },
  storyThumb: {
    width: 120,
    height: 68,
    flexShrink: 0,
    borderRadius: 6,
    overflow: "hidden",
    background: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center top",
    display: "block",
  },
  thumbIcon: {
    fontSize: "2rem",
  },
  emptyState: {
    textAlign: "center",
    padding: "5rem 1rem",
  },
  emptyTitle: {
    fontFamily: "'Georgia', serif",
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#191919",
    marginBottom: 8,
  },
  emptySub: {
    fontSize: "0.9rem",
    color: "#999",
    marginBottom: "1.5rem",
  },
  titleInput: {
    border: "none",
    borderBottom: "1px solid #e8e8e8",
    borderRadius: 0,
    padding: "0.5rem 0",
    fontSize: "1.5rem",
    fontFamily: "'Georgia', serif",
    fontWeight: 700,
    color: "#191919",
    background: "transparent",
    boxShadow: "none",
  },
  subtitleInput: {
    border: "none",
    borderBottom: "1px solid #e8e8e8",
    borderRadius: 0,
    padding: "0.5rem 0",
    fontSize: "1.05rem",
    color: "#6b6b6b",
    background: "transparent",
    boxShadow: "none",
    resize: "none",
  },
  bodyInput: {
    borderRadius: 6,
    fontSize: "0.92rem",
    fontFamily: "'Georgia', serif",
    lineHeight: 1.85,
    color: "#191919",
    padding: "0.75rem",
    resize: "vertical",
    minHeight: 520,
  },
  settingsPanel: {
    background: "#fafafa",
    border: "1px solid #e8e8e8",
    borderRadius: 10,
    padding: "1.25rem",
  },
  settingsSectionLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    color: "#999",
    marginBottom: "0.75rem",
  },
  fieldLabel: {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#444",
    marginBottom: 4,
  },
  slugRow: {
    display: "flex",
    alignItems: "center",
    gap: 0,
  },
  slugPrefix: {
    flex: "0 0 auto",
    maxWidth: "55%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.78rem",
    color: "#777",
    background: "#f3f4f6",
    border: "1px solid #ddd",
    borderRight: 0,
    borderRadius: "6px 0 0 6px",
    padding: "6px 8px",
    lineHeight: 1.5,
  },
  slugInput: {
    borderRadius: "0 6px 6px 0",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "0.82rem",
  },
  slugHint: {
    marginTop: 6,
    fontSize: "0.74rem",
    lineHeight: 1.45,
    color: "#777",
  },
  slugHistory: {
    marginTop: 6,
    fontSize: "0.74rem",
    lineHeight: 1.8,
    color: "#555",
  },
  slugHistoryItem: {
    display: "inline-block",
    marginRight: 6,
    fontSize: "0.72rem",
    background: "#f3f4f6",
    borderRadius: 4,
    padding: "1px 5px",
  },
  select: {
    fontSize: "0.88rem",
    borderColor: "#e0e0e0",
  },
  input: {
    fontSize: "0.88rem",
    borderColor: "#e0e0e0",
  },
  cancelBtn: {
    background: "none",
    border: "1px solid #e0e0e0",
    borderRadius: 99,
    padding: "8px 20px",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
    color: "#444",
    marginRight: 8,
  },
  publishBtn: {
    background: "#1a8917",
    color: "white",
    border: "none",
    borderRadius: 99,
    padding: "8px 22px",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  skeletonRow: {
    display: "flex",
    gap: "1.25rem",
    padding: "2rem 0",
    borderTop: "1px solid #e8e8e8",
    alignItems: "center",
  },
  skeletonBlock: {
    background: "linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)",
    backgroundSize: "400px 100%",
    animation: "shimmer 1.4s ease infinite",
    borderRadius: 6,
  },
  attachmentPreview: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    background: "#f0f7f0",
    border: "1px solid #c8e6c9",
    borderRadius: 8,
    marginBottom: 8,
  },
  uploadAttachBtn: {
    width: "100%",
    padding: "10px 12px",
    border: "1px dashed #bbb",
    borderRadius: 6,
    background: "#fafafa",
    cursor: "pointer",
    fontSize: "0.82rem",
    color: "#555",
    fontWeight: 500,
    textAlign: "left",
  },
  removeAttachBtn: {
    background: "none",
    border: "none",
    color: "#c62828",
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    padding: "2px 6px",
    flexShrink: 0,
  },
  tiptapToolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    padding: "6px 8px",
    background: "#fafafa",
    border: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0",
    borderRadius: "8px 8px 0 0",
    position: "sticky",
    top: 0,
    zIndex: 20,
  },
  tbSelect: {
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "2px 4px",
    fontSize: "0.78rem",
    cursor: "pointer",
    color: "#333",
    background: "#fff",
    height: 26,
    maxWidth: 130,
  },
  tbBtn: {
    background: "transparent",
    border: "none",
    borderRadius: 4,
    padding: "3px 7px",
    fontSize: "0.82rem",
    cursor: "pointer",
    color: "#222",
    lineHeight: 1.4,
    minWidth: 26,
  },
  tbDivider: {
    display: "inline-block",
    width: 1,
    height: 18,
    background: "#ddd",
    margin: "0 4px",
  },
  tablePickerDropdown: {
    position: "absolute",
    top: "110%",
    left: 0,
    zIndex: 9999,
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 10,
    boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
  },
  tiptapScrollWrap: {
    overflowY: "auto",
    maxHeight: "calc(82vh - 220px)",
    border: "1px solid #e0e0e0",
    borderRadius: "0 0 8px 8px",
    background: "#fff",
  },
  tiptapEditorWrap: {
    minHeight: 480,
    cursor: "text",
    background: "#fff",
  },
};

export default BlogManager;
